/**
 * Estimating what a buyer outside the EU customs territory pays on delivery.
 *
 * We ship DAP, so the buyer is the importer of record and the carrier collects
 * import VAT, any duty and a clearance fee at the door.
 * `CartUserProductImportCharges` already warns that this is coming. This module
 * is the next step: putting a number on it, because "you may be charged" does
 * not help someone decide whether a EUR 695 sofa is worth it when the honest
 * answer is "plus about EUR 174".
 *
 * Scope is deliberately narrow. Only GB, NO and CH are modelled, because those
 * are the only non-EU destinations we sell to and each has a rate we can state
 * with confidence. Anywhere else returns null and the existing qualitative
 * warning stands on its own.
 *
 * This is NOT a customs ruling. Every rate below needs sign-off from someone
 * who does this for a living before it goes in front of buyers. See REVIEW.
 *
 * No antique handling. GB taxes antiques over 100 years old at an effective 5%
 * rather than 20%, but Whoppah does not sell antiques: the catalogue is vintage
 * and design, which is decades old, not centuries. Modelling a rate we never
 * hit would add a branch nobody can test and a field (`year_of_purchase`, the
 * year the SELLER bought it) that could not drive it correctly anyway.
 */

/** Duty treatment differs by commodity, so products are bucketed by category. */
export type DutyGroup =
  /** Furniture, lighting, mirrors, rugs. HS 9403 / 9405 / 7009 / 5702-5705. */
  | "furnishings"
  /** Handbags and other leather goods. HS 4202. */
  | "leatherGoods"
  /** Paintings, sculpture, collectors' items. HS chapter 97. */
  | "art";

export type ImportChargeRule = {
  /** Import VAT, as a fraction of the customs value. */
  vatRate: number;
  /** Duty as a fraction of the customs value, per commodity bucket. */
  dutyRates: Record<DutyGroup, number>;
  /**
   * Below this customs value (EUR) the destination collects nothing at the
   * border. Above it, the buyer pays on delivery.
   */
  deMinimisEur: number;
  /**
   * Whether import VAT is charged on the delivery cost as well as the goods.
   * True everywhere we model: the customs value is CIF, not ex-works.
   */
  vatOnShipping: boolean;
};

/**
 * REVIEW (Omer): these are the numbers to challenge first.
 *
 * GB  VAT 20%, flat. Duty: furniture and art are 0% under the UK Global Tariff
 *     regardless of origin. Leather handbags (4202.21) are 3%. We assume NO
 *     preferential
 *     origin, because our sellers are consumers and will not produce an EU
 *     statement of origin, so the TCA 0% rate is not available to them in
 *     practice. If we ever automate origin statements this becomes 0%.
 *
 * NO  VAT 25%. Norway levies duty mainly on clothing, textiles and food;
 *     furniture, leather goods and art are duty-free. The VOEC scheme would
 *     move collection to checkout below NOK 3,000, but that is a seller-side
 *     registration we do not hold, so from the buyer's point of view the border
 *     collects. Revisit when the VOEC number lands.
 *
 * CH  VAT 8.1%. Swiss duty is assessed by WEIGHT, not value, and is small for
 *     these categories. Modelled as 0% rather than guessed, and the copy says
 *     "from", so we under-promise rather than over-promise. De minimis: the
 *     Swiss customs office does not collect import VAT of CHF 5 or less, i.e.
 *     a consignment up to about CHF 62 at 8.1%, held as EUR 60 so that a weak
 *     franc never makes us claim "nothing to pay" on a parcel that is taxed.
 *     (This was CHF 150 until 2026-10-27, which is the travellers' allowance,
 *     not the rule for parcels.)
 */
export const IMPORT_CHARGE_RULES: Record<string, ImportChargeRule> = {
  GB: {
    vatRate: 0.2,
    dutyRates: { furnishings: 0, leatherGoods: 0.03, art: 0 },
    deMinimisEur: 0,
    vatOnShipping: true,
  },
  NO: {
    vatRate: 0.25,
    dutyRates: { furnishings: 0, leatherGoods: 0, art: 0 },
    deMinimisEur: 0,
    vatOnShipping: true,
  },
  CH: {
    vatRate: 0.081,
    dutyRates: { furnishings: 0, leatherGoods: 0, art: 0 },
    deMinimisEur: 60,
    vatOnShipping: true,
  },
};

/** Destinations we can put a number on. Everything else gets the warning only. */
export const ESTIMATED_IMPORT_CHARGE_DESTINATIONS =
  Object.keys(IMPORT_CHARGE_RULES);

export type ImportChargeEstimate = {
  /** Destination ISO code the estimate was computed for. */
  destinationCountry: string;
  /** The value the destination assesses: goods plus delivery. */
  customsValue: number;
  vat: number;
  duty: number;
  /** vat + duty. Excludes the carrier clearance fee, which we cannot know. */
  total: number;
};

type EstimateInput = {
  destinationCountry?: string | null;
  /** Item price in EUR. */
  itemPrice: number;
  /** Delivery price in EUR. Zero for free delivery. */
  shippingPrice?: number;
  dutyGroup: DutyGroup;
};

/**
 * Returns null whenever we cannot stand behind a number: no destination, a
 * destination we do not model, or a consignment under the local de minimis.
 * Callers must treat null as "say nothing", never as "zero".
 */
export const estimateImportCharges = ({
  destinationCountry,
  itemPrice,
  shippingPrice = 0,
  dutyGroup,
}: EstimateInput): ImportChargeEstimate | null => {
  if (!destinationCountry) {
    return null;
  }

  const code = destinationCountry.toUpperCase();
  const rule = IMPORT_CHARGE_RULES[code];
  if (!rule) {
    return null;
  }

  const customsValue = itemPrice + (rule.vatOnShipping ? shippingPrice : 0);
  if (customsValue <= rule.deMinimisEur) {
    return null;
  }

  // Duty is assessed on goods plus freight, and VAT is then charged on the
  // duty-inclusive value. Getting that order wrong understates the bill.
  const duty = customsValue * rule.dutyRates[dutyGroup];
  const vat = (customsValue + duty) * rule.vatRate;

  return {
    destinationCountry: code,
    customsValue,
    vat,
    duty,
    total: vat + duty,
  };
};
